import { useEffect, useMemo, useState } from 'react';
import { formsApi } from '../api/formsApi';
import { config } from '../config';

/** Configure iframe embedding for a form: allowed parent origins + a copy-paste snippet. */
export function EmbedPanel({ formId }: { formId: string }) {
  const [allowedOrigins, setAllowedOrigins] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    formsApi
      .getEmbed(formId)
      .then((cfg) => setAllowedOrigins(cfg.allowedOrigins ?? ''))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load embed config'));
  }, [formId]);

  const formUrl = `${config.publicFormsUrl.replace(/\/$/, '')}/f/${formId}`;

  const snippet = useMemo(
    () =>
      `<iframe id="forms-${formId}" src="${formUrl}" style="width:100%;border:0" title="Form"></iframe>
<script>
  window.addEventListener('message', function (e) {
    var d = e.data;
    if (d && d.source === 'forms' && d.type === 'forms:resize' && d.formId === '${formId}') {
      var f = document.getElementById('forms-${formId}');
      if (f) f.style.height = d.height + 'px';
    }
  });
</script>`,
    [formId, formUrl],
  );

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await formsApi.updateEmbed(formId, { allowedOrigins: allowedOrigins.trim() || null });
      setStatus('Embed settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save embed settings');
    } finally {
      setBusy(false);
    }
  }

  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="secret-links">
      <h3>Embed</h3>
      <p className="muted">
        Embed this form in another site with the snippet below. It auto-resizes to fit the
        content. Restrict which sites may embed it by listing their origins — leave blank to
        allow any site.
      </p>

      <form className="webhook-form" onSubmit={save}>
        <label className="field">
          Allowed parent origins <span className="muted">(comma-separated; blank = anywhere)</span>
          <input
            type="text"
            placeholder="https://www.charlie-mac.com, https://intranet.charlie-mac.com"
            value={allowedOrigins}
            onChange={(e) => setAllowedOrigins(e.target.value)}
          />
        </label>
        <div className="actions">
          <button type="submit" disabled={busy}>Save embed settings</button>
          {status && <span className="success">{status}</span>}
          {error && <span className="error">{error}</span>}
        </div>
      </form>

      <label className="field">
        Embed snippet
        <textarea className="embed-snippet" readOnly rows={9} value={snippet} />
      </label>
      <div className="actions">
        <button type="button" onClick={copySnippet}>{copied ? 'Copied' : 'Copy snippet'}</button>
      </div>
    </div>
  );
}
